import { RefObject } from 'react';
import { Button, Field, Option } from '@fluentui/react-components';
import { ArrowUploadRegular, DeleteRegular } from '@fluentui/react-icons';
import { Control, FieldErrors } from 'react-hook-form';

import { ControlledField } from '@shared/components';

import { FIELD_LABELS, TYPE_OPTIONS } from '../../constants';
import { getTypeLabel } from '../../helpers';
import { TLiteratureFormData } from './literatureDialogForm';

type TProps = {
  control: Control<TLiteratureFormData>;
  errors: FieldErrors<TLiteratureFormData>;
  pdfFile: File | undefined;
  setPdfFile: (file: File | undefined) => void;
  fileInputRef: RefObject<HTMLInputElement>;
};

const ManualTab = ({ control, errors, pdfFile, setPdfFile, fileInputRef }: TProps) => (
  <div className="grid grid-cols-6 gap-x-3 gap-y-3 items-start">
    <ControlledField
      label={FIELD_LABELS.type}
      name="type"
      control={control}
      errors={errors}
      required
      inputType="dropdown"
      getLabel={getTypeLabel}
      placeholder="Select article type"
      fieldClassName="col-span-2"
    >
      {TYPE_OPTIONS.map((opt) => (
        <Option key={opt.value} value={opt.value}>
          {opt.label}
        </Option>
      ))}
    </ControlledField>

    <Field label="PDF attachment" className="col-span-4">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          appearance="secondary"
          icon={<ArrowUploadRegular />}
          className="flex-1"
          onClick={() => fileInputRef.current?.click()}
        >
          {pdfFile ? pdfFile.name : 'Upload PDF'}
        </Button>
        {pdfFile && (
          <Button
            type="button"
            appearance="subtle"
            icon={<DeleteRegular />}
            aria-label="Remove PDF"
            onClick={() => setPdfFile(undefined)}
          />
        )}
      </div>
    </Field>

    <ControlledField
      label={FIELD_LABELS.title}
      name="title"
      control={control}
      errors={errors}
      required
      inputType="input"
      placeholder="Title"
      fieldClassName="col-span-6"
    />

    <ControlledField
      label={FIELD_LABELS.authors}
      name="authors"
      control={control}
      errors={errors}
      inputType="input"
      placeholder="Comma-separated author names"
      fieldClassName="col-span-6"
    />

    <ControlledField
      label={FIELD_LABELS.containerTitle}
      name="containerTitle"
      control={control}
      errors={errors}
      inputType="input"
      placeholder="Journal or book title"
      fieldClassName="col-span-3"
    />

    <ControlledField
      label={FIELD_LABELS.publisher}
      name="publisher"
      control={control}
      errors={errors}
      inputType="input"
      fieldClassName="col-span-3"
    />

    {/* Nested 4-col sub-grid so year/volume/issue/pages render as one Vancouver locator unit.
        Using col-span-1 directly in the outer 6-col grid makes Fluent Input narrower than its min-width. */}
    <div className="col-span-6 grid grid-cols-4 gap-x-3 items-start">
      <ControlledField
        label={FIELD_LABELS.publicationYear}
        name="publicationYear"
        control={control}
        errors={errors}
        inputType="number"
        placeholder="e.g. 2024"
      />
      <ControlledField label={FIELD_LABELS.volume} name="volume" control={control} errors={errors} inputType="input" />
      <ControlledField label={FIELD_LABELS.issue} name="issue" control={control} errors={errors} inputType="input" />
      <ControlledField
        label={FIELD_LABELS.pages}
        name="pages"
        control={control}
        errors={errors}
        inputType="input"
        placeholder="e.g. 1-15"
      />
    </div>

    <ControlledField
      label={FIELD_LABELS.doi}
      name="doi"
      control={control}
      errors={errors}
      inputType="input"
      placeholder="e.g. 10.1234/example"
      fieldClassName="col-span-3"
    />

    <ControlledField
      label={FIELD_LABELS.pmid}
      name="pmid"
      control={control}
      errors={errors}
      inputType="input"
      fieldClassName="col-span-3"
    />

    <ControlledField
      label={FIELD_LABELS.url}
      name="url"
      control={control}
      errors={errors}
      inputType="input"
      placeholder="https://..."
      fieldClassName="col-span-3"
    />

    <ControlledField
      label={FIELD_LABELS.accessDate}
      name="accessDate"
      control={control}
      errors={errors}
      inputType="date"
      fieldClassName="col-span-3"
    />
  </div>
);

export { ManualTab };
